import React, { useState } from "react";
import { useI18n } from "../lib/i18n";
import { Copy, Check } from "@phosphor-icons/react";
import { copyText } from "../lib/export";

function CopyBlock({ value, label, testid, multiline = false }) {
  const [done, setDone] = useState(false);
  const { t } = useI18n();
  const onCopy = async () => {
    await copyText(value);
    setDone(true);
    setTimeout(() => setDone(false), 1400);
  };
  return (
    <div className="rounded-2xl bg-cream-warm/50 border border-line p-4" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="kicker-label">{label}</div>
        <button
          onClick={onCopy}
          className="text-xs font-medium text-forest hover:text-forest-dark flex items-center gap-1.5"
          data-testid={`${testid}-copy`}
        >
          {done ? <Check size={14} weight="bold" /> : <Copy size={14} weight="duotone" />}
          {done ? t("copied") : t("copy")}
        </button>
      </div>
      <div className={`text-[14px] text-ink leading-relaxed ${multiline ? "whitespace-pre-wrap" : ""}`}>
        {value || <span className="text-ink-soft/60">—</span>}
      </div>
    </div>
  );
}

function ListBlock({ items, label, testid }) {
  const { t } = useI18n();
  const [copiedIdx, setCopiedIdx] = useState(-1);
  const copyOne = async (text, idx) => {
    await copyText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(-1), 1200);
  };
  return (
    <div className="rounded-2xl bg-cream-warm/50 border border-line p-4" data-testid={testid}>
      <div className="kicker-label mb-3">{label}</div>
      <div className="space-y-2">
        {(items || []).map((it, i) => (
          <div key={i} className="flex items-start justify-between gap-3 group" data-testid={`${testid}-item-${i}`}>
            <div className="text-[14px] text-ink leading-relaxed flex-1">{it}</div>
            <button
              onClick={() => copyOne(it, i)}
              className="opacity-0 group-hover:opacity-100 text-xs text-forest hover:text-forest-dark flex items-center gap-1 transition"
              data-testid={`${testid}-copy-${i}`}
            >
              {copiedIdx === i ? <Check size={12} weight="bold" /> : <Copy size={12} weight="duotone" />}
              {copiedIdx === i ? t("copied") : t("copy")}
            </button>
          </div>
        ))}
        {(!items || items.length === 0) && <div className="text-ink-soft/60 text-sm">—</div>}
      </div>
    </div>
  );
}

export default function ContentPanel({ carousel }) {
  const { t, lang } = useI18n();
  if (!carousel) return null;
  const hashtagString = (carousel.hashtags || []).join(" ");
  const altTexts = (carousel.slides || []).map((s, i) => ({
    label: lang === "el" ? `Slide ${i + 1}` : `Slide ${i + 1}`,
    value: (s.alt_text || "").trim(),
  })).filter((x) => x.value);

  return (
    <div className="p-6 space-y-4" data-testid="content-panel">
      <div className="flex items-center gap-3 mb-2">
        <div className="kicker-label">{t("editor_content")}</div>
        <div className="flex-1 border-t border-line" />
      </div>

      <CopyBlock label={t("caption_short")} value={carousel.caption_short} testid="caption-short" multiline />
      <CopyBlock label={t("caption_seo")} value={carousel.caption_seo} testid="caption-seo" multiline />
      <CopyBlock label={t("hashtags")} value={hashtagString} testid="hashtags-block" />
      {altTexts.length > 0 && (
        <div className="rounded-2xl bg-cream-warm/50 border border-line p-4" data-testid="alt-texts-block">
          <div className="kicker-label mb-1">
            {lang === "el" ? "ALT TEXT (Accessibility)" : "ALT TEXT (Accessibility)"}
          </div>
          <div className="text-[11px] text-ink-soft mb-3 leading-relaxed">
            {lang === "el"
              ? "Στο Insta: Advanced Settings → Accessibility → Write alt text. Επικόλλησε το alt text κάθε slide ξεχωριστά για boost στο reach + accessibility."
              : "In Insta: Advanced Settings → Accessibility → Write alt text. Paste each slide's alt text separately for reach + accessibility boost."}
          </div>
          <div className="space-y-2">
            {altTexts.map((it, i) => (
              <AltTextRow key={i} label={it.label} value={it.value} idx={i} />
            ))}
          </div>
        </div>
      )}
      <ListBlock label={t("alt_hooks")} items={carousel.alt_hooks} testid="alt-hooks" />
      <ListBlock label={t("alt_ctas")} items={carousel.alt_ctas} testid="alt-ctas" />
    </div>
  );
}

function AltTextRow({ label, value, idx }) {
  const { t } = useI18n();
  const [done, setDone] = useState(false);
  const onCopy = async () => {
    await copyText(value);
    setDone(true);
    setTimeout(() => setDone(false), 1400);
  };
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-line/50 last:border-0" data-testid={`alt-text-row-${idx}`}>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-forest mb-0.5">{label}</div>
        <div className="text-[13px] text-ink leading-snug">{value}</div>
      </div>
      <button
        onClick={onCopy}
        className="text-xs font-medium text-forest hover:text-forest-dark flex items-center gap-1.5 flex-shrink-0"
        data-testid={`alt-text-copy-${idx}`}
      >
        {done ? <Check size={12} weight="bold" /> : <Copy size={12} weight="duotone" />}
        {done ? t("copied") : t("copy")}
      </button>
    </div>
  );
}
